import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Save, 
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  FileSignature
} from 'lucide-react';
import { profileAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const schema = yup.object({
  companyName: yup.string().required('Company name is required'),
  gstNumber: yup.string().test('gst', 'Invalid GST number format', (value) => {
    if (!value || value.trim() === '') return true; // Optional
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value.toUpperCase());
  }),
  panNumber: yup.string().test('pan', 'Invalid PAN number format', (value) => {
    if (!value || value.trim() === '') return true; // Optional
    return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase());
  }),
  businessType: yup.string().required('Business type is required'),
  contactNumber: yup.string().test('mobile', 'Invalid mobile number', (value) => {
    if (!value || value.length === 0) return true;
    return /^[6-9]\d{9}$/.test(value);
  }),
  email: yup.string().required('Email is required').email('Invalid email format'),
  address: yup.object({
    street: yup.string().required('Street address is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    pincode: yup.string().required('Pincode is required').matches(/^[1-9][0-9]{5}$/, 'Invalid pincode'),
    country: yup.string().required('Country is required'),
  }),
  bankDetails: yup.object({
    bankName: yup.string(),
    accountNumber: yup.string(),
    ifscCode: yup.string(),
    branch: yup.string(),
  }),
  invoiceNumberPrefix: yup.string().required('Invoice prefix is required'),
  receiptNumberPrefix: yup.string().required('Receipt prefix is required'),
  defaultGstRate: yup.number().required('GST rate is required'),
  termsAndConditions: yup.string(),
  receiptTermsAndConditions: yup.string(),
});

const CompanyProfile: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { checkProfileComplete, profileComplete } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      address: {
        country: 'India'
      },
      invoiceNumberPrefix: 'INV',
      receiptNumberPrefix: 'REC',
      defaultGstRate: 18,
      termsAndConditions: 'We declare that this invoice shows the actual price of the goods/services described and that all particulars are true and correct.',
      receiptTermsAndConditions: 'This receipt acknowledges that the payment has been received in full. All disputes are subject to the jurisdiction of the local courts.'
    }
  });

  const logo = (watch as any)('logo');
  const digitalSignature = (watch as any)('digitalSignature');
  const pincode = (watch as any)('address.pincode');

  const fetchProfile = useCallback(async () => {
    try {
      const response = await profileAPI.get();
      if (response.data) {
        reset(response.data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setMessage({ type: 'error', text: 'Failed to load company profile' });
    } finally {
      setLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Auto-fill city and state based on pin code
  useEffect(() => {
    const fetchLocationData = async () => {
      if (pincode && pincode.length === 6 && /^[1-9][0-9]{5}$/.test(pincode)) {
        try {
          const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
          const data = await response.json();
          
          if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
            const postOffice = data[0].PostOffice[0];
            setValue('address.city' as any, postOffice.District || '');
            setValue('address.state' as any, postOffice.State || '');
          }
        } catch (error) {
          console.error('Error fetching location data:', error);
        }
      }
    };

    fetchLocationData();
  }, [pincode, setValue]);

  const onSubmit = async (data: any) => {
    setSaving(true);
    setMessage(null);
    
    try {
      await profileAPI.update(data);
      setMessage({ type: 'success', text: 'Company profile updated successfully!' });
      
      // Check if profile is now complete
      const isComplete = await checkProfileComplete();
      
      // If profile is complete and was incomplete before, redirect to dashboard
      if (isComplete) {
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update company profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingLogo(true);
    try {
      const response = await profileAPI.uploadLogo(file);
      setValue('logo' as any, response.data.logo);
      setMessage({ type: 'success', text: 'Logo uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading logo:', error);
      setMessage({ type: 'error', text: 'Failed to upload logo' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingSignature(true);
    try {
      const response = await profileAPI.uploadSignature(file);
      setValue('digitalSignature' as any, response.data.digitalSignature);
      setMessage({ type: 'success', text: 'Digital signature uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading signature:', error);
      setMessage({ type: 'error', text: 'Failed to upload signature' });
    } finally {
      setUploadingSignature(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <Building2 className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
          <p className="text-gray-600">Manage your company information and settings</p>
        </div>
      </div>

      {/* Incomplete Profile Warning */}
      {!profileComplete && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-lg bg-yellow-50 text-yellow-800 border border-yellow-200 flex items-center space-x-2"
        >
          <AlertCircle className="h-5 w-5" />
          <span className="font-semibold">Please complete your company profile to continue. Fill in all required fields below.</span>
        </motion.div>
      )}

      {/* Message */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span>{message.text}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Company Name *</label>
              <input
                type="text"
                {...register('companyName' as any)}
                className="form-input"
                placeholder="Enter company name"
              />
              {(errors as any).companyName && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).companyName.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Business Type *</label>
              <select {...register('businessType' as any)} className="form-input">
                <option value="">Select business type</option>
                <option value="Proprietorship">Proprietorship</option>
                <option value="Partnership">Partnership</option>
                <option value="Private Limited">Private Limited</option>
                <option value="Public Limited">Public Limited</option>
                <option value="LLP">LLP</option>
                <option value="Other">Other</option>
              </select>
              {(errors as any).businessType && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).businessType.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">GST Number</label>
              <input
                type="text"
                {...register('gstNumber' as any)}
                className="form-input"
                placeholder="Enter GST number (e.g., 22AAAAA0000A1Z5)"
                style={{ textTransform: 'uppercase' }}
              />
              {(errors as any).gstNumber && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).gstNumber.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">PAN Number</label>
              <input
                type="text"
                {...register('panNumber' as any)}
                className="form-input"
                placeholder="Enter PAN number (e.g., AAAAA0000A)"
                style={{ textTransform: 'uppercase' }}
              />
              {(errors as any).panNumber && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).panNumber.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Contact Number</label>
              <input
                type="tel"
                {...register('contactNumber' as any)}
                className="form-input"
                placeholder="9876543210"
              />
              {(errors as any).contactNumber && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).contactNumber.message}</p>
              )}
            </div>

            <div>
              <label className="form-label">Email *</label>
              <input
                type="email"
                {...register('email' as any)}
                className="form-input"
                placeholder="company@example.com"
              />
              {(errors as any).email && (
                <p className="mt-1 text-sm text-red-600">{(errors as any).email.message}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Address */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Address</h2>
          
          <div className="space-y-4">
            <div>
              <label className="form-label">Street Address *</label>
              <input
                type="text"
                  {...register('address.street' as any)}
                className="form-input"
                placeholder="Enter street address"
              />
              {(errors.address as any)?.street && (
                <p className="mt-1 text-sm text-red-600">{(errors.address as any).street.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">City *</label>
                <input
                  type="text"
                  {...register('address.city' as any)}
                  className="form-input"
                  placeholder="Enter city"
                />
                {(errors.address as any)?.city && (
                  <p className="mt-1 text-sm text-red-600">{(errors.address as any).city.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">State *</label>
                <input
                  type="text"
                  {...register('address.state' as any)}
                  className="form-input"
                  placeholder="Enter state"
                />
                {(errors.address as any)?.state && (
                  <p className="mt-1 text-sm text-red-600">{(errors.address as any).state.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">Pincode *</label>
                <input
                  type="text"
                  {...register('address.pincode' as any)}
                  className="form-input"
                  placeholder="123456"
                />
                {(errors.address as any)?.pincode && (
                  <p className="mt-1 text-sm text-red-600">{(errors.address as any).pincode.message}</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bank Details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Bank Details (Optional)</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Bank Name</label>
              <input
                type="text"
                {...register('bankDetails.bankName' as any)}
                className="form-input"
                placeholder="Enter bank name"
              />
            </div>

            <div>
              <label className="form-label">Account Number</label>
              <input
                type="text"
                {...register('bankDetails.accountNumber' as any)}
                className="form-input"
                placeholder="Enter account number"
              />
            </div>

            <div>
              <label className="form-label">IFSC Code</label>
              <input
                type="text"
                {...register('bankDetails.ifscCode' as any)}
                className="form-input"
                placeholder="SBIN0001234"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div>
              <label className="form-label">Branch</label>
              <input
                type="text"
                {...register('bankDetails.branch' as any)}
                className="form-input"
                placeholder="Enter branch name"
              />
            </div>
          </div>
        </motion.div>

        {/* Logo and Signature */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Logo & Digital Signature</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Upload */}
            <div>
              <label className="form-label">Company Logo</label>
              <div className="space-y-4">
                {logo && (
                  <div className="flex items-center space-x-4">
                    <img
                      src={typeof logo === 'string' ? logo : (logo?.url || logo)}
                      alt="Company Logo"
                      className="h-20 w-20 object-contain border border-gray-200 rounded-lg"
                    />
                    <div>
                      <p className="text-sm text-gray-600">Current logo</p>
                      <button
                        type="button"
                        onClick={() => setValue('logo' as any, undefined)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    disabled={uploadingLogo}
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 ${
                      uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <ImageIcon className="h-4 w-4" />
                    <span>{uploadingLogo ? 'Uploading...' : 'Upload Logo'}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Signature Upload */}
            <div>
              <label className="form-label">Digital Signature</label>
              <div className="space-y-4">
                {digitalSignature && (
                  <div className="flex items-center space-x-4">
                    <img
                      src={typeof digitalSignature === 'string' ? digitalSignature : (digitalSignature?.url || digitalSignature)}
                      alt="Digital Signature"
                      className="h-20 w-32 object-contain border border-gray-200 rounded-lg"
                    />
                    <div>
                      <p className="text-sm text-gray-600">Current signature</p>
                      <button
                        type="button"
                        onClick={() => setValue('digitalSignature' as any, undefined)}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSignatureUpload}
                    className="hidden"
                    id="signature-upload"
                    disabled={uploadingSignature}
                  />
                  <label
                    htmlFor="signature-upload"
                    className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 ${
                      uploadingSignature ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <FileSignature className="h-4 w-4" />
                    <span>{uploadingSignature ? 'Uploading...' : 'Upload Signature'}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Invoice Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="form-label">Invoice Number Prefix *</label>
              <input
                type="text"
                {...register('invoiceNumberPrefix')}
                className="form-input"
                placeholder="INV"
                style={{ textTransform: 'uppercase' }}
              />
              {errors.invoiceNumberPrefix && (
                <p className="mt-1 text-sm text-red-600">{errors.invoiceNumberPrefix.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                This will be used to generate invoice numbers like INV000001, INV000002, etc.
              </p>
            </div>

            <div>
              <label className="form-label">Default GST Rate *</label>
              <select
                {...register('defaultGstRate', { valueAsNumber: true })}
                className="form-input"
              >
                <option value={0}>0% (Exempt)</option>
                <option value={3}>3%</option>
                <option value={5}>5%</option>
                <option value={12}>12%</option>
                <option value={18}>18%</option>
                <option value={28}>28%</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                This GST rate will be applied by default when creating new invoices. CGST and SGST will be calculated as half of this rate.
              </p>
            </div>

            <div>
              <label className="form-label">Terms & Conditions</label>
              <textarea
                {...register('termsAndConditions')}
                rows={4}
                className="form-input"
                placeholder="Enter terms and conditions"
              />
            </div>
          </div>
        </motion.div>

        {/* Receipt Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="card"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Receipt Settings</h2>
          
          <div className="space-y-4">
            <div>
              <label className="form-label">Receipt Number Prefix *</label>
              <input
                type="text"
                {...register('receiptNumberPrefix')}
                className="form-input"
                placeholder="REC"
                style={{ textTransform: 'uppercase' }}
              />
              {errors.receiptNumberPrefix && (
                <p className="mt-1 text-sm text-red-600">{errors.receiptNumberPrefix.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                This will be used to generate receipt numbers like REC000001, REC000002, etc.
              </p>
            </div>

            <div>
              <label className="form-label">Terms & Conditions</label>
              <textarea
                {...register('receiptTermsAndConditions')}
                rows={4}
                className="form-input"
                placeholder="Enter terms and conditions for receipts"
              />
            </div>
          </div>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex justify-end"
        >
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>{saving ? 'Saving...' : 'Save Profile'}</span>
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default CompanyProfile;
